import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { CurrencySchema, WeightSchema } from "../outbound-labels/util";
import { RedoShopifyCountryCode } from "./fulfillment-country-code";
import { RedoShopifyDeliveryMethodType } from "./fulfillment-delivery-method-type";
import { RedoShopifyFulfillmentDisplayStatus } from "./fulfillment-display-status";
import { RedoShopifyFulfillmentHoldReason } from "./fulfillment-hold-reason";
import { RedoShopifyFulfillmentOrderAction } from "./fulfillment-order-action";
import { RedoShopifyFulfillmentOrderRequestStatus } from "./fulfillment-order-request-status";
import { RedoShopifyFulfillmentOrderStatus } from "./fulfillment-order-status";
import { RedoShopifyFulfillmentStatus } from "./fulfillment-status";
import { RedoShopifyOrderDisplayFinancialStatus } from "./order-display-financial-status";
import {
  RedoShopifyOrderDisputeInitiatedAs,
  RedoShopifyOrderDisputeStatus,
} from "./order-disputes";
import { OrderTransactionKind } from "./order-transaction-kind";

const FulfillmentOrderAssignedLocationSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  countryCode: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }),
  location: z.object({ id: z.string() }).nullish(),
  name: z.string(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
});

const FulfillmentOrderDestinationSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  company: z.string().nullish(),
  countryCode: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).nullish(),
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  location: z.object({ id: z.string() }).nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  zip: z.string().nullish(),
});

const FulfillmentDeliveryMethodSchema = z.object({
  maxDeliveryDateTime: z.coerce.date().nullish(),
  methodType: createEnum(RedoShopifyDeliveryMethodType, {
    enumName: "RedoShopifyDeliveryMethodType",
  }),
  minDeliveryDateTime: z.coerce.date().nullish(),
  presentedName: z.string().nullish(),
  serviceCode: z.string().nullish(),
  additionalInformation: z.object({
    instructions: z.string().nullish(),
    phone: z.string().nullish(),
  }),
});

const FulfillmentHoldsSchema = z.object({
  displayReason: z.string(),
  id: z.string(),
  reason: createEnum(RedoShopifyFulfillmentHoldReason, {
    enumName: "RedoShopifyFulfillmentHoldReason",
  }),
  reasonNotes: z.string().nullish(),
});

export const FulfillmentOrderSupportedActionSchema = z.object({
  action: createEnum(RedoShopifyFulfillmentOrderAction, {
    enumName: "RedoShopifyFulfillmentOrderAction",
  }),
  externalUrl: z.string().nullish(),
});

const MoneyBagSchema = z.object({
  shopMoney: z.object({ amount: z.string(), currencyCode: CurrencySchema }),
});
const MoneyBagWithPresentedSchema = MoneyBagSchema.extend({
  presentmentMoney: z.object({
    amount: z.string(),
    currencyCode: createEnum(Currency, { enumName: "Currency" }),
  }),
});

export const RiskSchema = z.object({
  assessments: z.array(
    z.object({
      facts: z.array(
        z.object({
          description: z.string().nullish(),
          sentiment: z.string().nullish(),
        }),
      ),
      riskLevel: z.string().nullish(),
    }),
  ),
});

export type OrderRisk = z.infer<typeof RiskSchema>;
export interface IRisk extends z.ZodType<OrderRisk> {}

const RawFulfillmentOrderLineItemSchema = z.object({
  financialSummaries: z.array(
    z.object({
      approximateDiscountedUnitPriceSet: MoneyBagSchema,
      discountAllocations: z.array(
        z.object({ approximateAllocatedAmountPerItem: MoneyBagSchema }),
      ),
      originalUnitPriceSet: MoneyBagSchema,
      quantity: z.number(),
    }),
  ),
  id: z.string(),
  productTitle: z.string(),
  variantTitle: z.string().nullish(),
  sku: z.string().nullish(),
  image: z.object({ url: z.string() }).nullish(),
  inventoryItemId: z.string().nullish(),
  lineItem: z.object({
    customAttributes: z.array(
      z.object({ key: z.string(), value: z.string().nullish() }),
    ),
    id: z.string(),
    originalUnitPriceSet: MoneyBagSchema,
    quantity: z.number(),
    taxLines: z.array(z.object({ priceSet: MoneyBagSchema })),
    duties: z
      .array(
        z.object({
          harmonizedSystemCode: z.string().nullish(),
          countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
            enumName: "RedoShopifyCountryCode",
          }).nullish(),
        }),
      )
      .nullish(),
  }),
  remainingQuantity: z.number(),
  requiresShipping: z.boolean(),
  totalQuantity: z.number(),
  vendor: z.string().nullish(),
  variant: z
    .object({
      displayName: z.string(),
      id: z.string(),
      inventoryQuantity: z.number().nullish(),
      bin: z.object({ value: z.string() }).nullish(),
      barcodeMeta: z.object({ value: z.string() }).nullish(),
      variantCustomDate1: z.object({ value: z.string() }).nullish(),
      variantCustomDate2: z.object({ value: z.string() }).nullish(),
      variantCustomDate3: z.object({ value: z.string() }).nullish(),
      variantCustomString1: z.object({ value: z.string() }).nullish(),
      variantCustomString2: z.object({ value: z.string() }).nullish(),
      variantCustomString3: z.object({ value: z.string() }).nullish(),
      variantCustomNumber1: z.object({ value: z.string() }).nullish(),
      variantCustomNumber2: z.object({ value: z.string() }).nullish(),
      variantCustomNumber3: z.object({ value: z.string() }).nullish(),
      product: z.object({
        id: z.string(),
        title: z.string(),
        vendor: z.string(),
        tags: z.array(z.string()).nullish(),
        bin: z.object({ value: z.string() }).nullish(),
        description: z.string(),
        productType: z.string().nullish(),
        productCustomDate1: z.object({ value: z.string() }).nullish(),
        productCustomDate2: z.object({ value: z.string() }).nullish(),
        productCustomDate3: z.object({ value: z.string() }).nullish(),
        productCustomString1: z.object({ value: z.string() }).nullish(),
        productCustomString2: z.object({ value: z.string() }).nullish(),
        productCustomString3: z.object({ value: z.string() }).nullish(),
        productCustomNumber1: z.object({ value: z.string() }).nullish(),
        productCustomNumber2: z.object({ value: z.string() }).nullish(),
        productCustomNumber3: z.object({ value: z.string() }).nullish(),
      }),
      inventoryItem: z.object({
        harmonizedSystemCode: z.string().nullish(),
        countryCodeOfOrigin: createEnum(RedoShopifyCountryCode, {
          enumName: "RedoShopifyCountryCode",
        }).nullish(),
        measurement: z.object({ weight: WeightSchema.nullish() }).nullish(),
      }),
      sku: z.string().nullish(),
      selectedOptions: z.array(
        z.object({ name: z.string(), value: z.string() }),
      ),
      barcode: z.string().nullish(),
      title: z.string(),
    })
    .nullish(),
  warnings: z.array(
    z.object({
      description: z.string().nullish(),
      title: z.string().nullish(),
    }),
  ),
  weight: WeightSchema.nullish(),
});

export type RawFulfillmentOrderLineItem = z.infer<
  typeof RawFulfillmentOrderLineItemSchema
>;

export const RawFulfillmentOrderLocationsForMoveSchema = z.object({
  nodes: z.array(
    z.object({
      location: z.object({ id: z.string(), name: z.string() }),
      movable: z.boolean(),
      message: z.string().nullish(),
      unavailableLineItemsCount: z.object({ count: z.number() }).nullish(),
    }),
  ),
});

export type RawFulfillmentOrderLocationsForMove = z.infer<
  typeof RawFulfillmentOrderLocationsForMoveSchema
>;

const LocalizationExtensionPurpose = ["SHIPPING", "TAX"] as const;

// Should match the requested fields from the fulfillment-fragments.ts
export const RawFulfillmentOrderSchema = z.object({
  id: z.string(),
  assignedLocation: FulfillmentOrderAssignedLocationSchema,
  channelId: z.string().nullish(),
  deliveryMethod: FulfillmentDeliveryMethodSchema.nullish(),
  destination: FulfillmentOrderDestinationSchema.nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  fulfillAt: z.coerce.date().nullish(),
  fulfillBy: z.coerce.date().nullish(),
  fulfillmentHolds: z.array(FulfillmentHoldsSchema),
  fulfillmentLineItems: z.array(RawFulfillmentOrderLineItemSchema),
  orderId: z.string(),
  orderName: z.string(),
  requestStatus: createEnum(RedoShopifyFulfillmentOrderRequestStatus, {
    enumName: "RedoShopifyFulfillmentOrderRequestStatus",
  }),
  status: createEnum(RedoShopifyFulfillmentOrderStatus, {
    enumName: "RedoShopifyFulfillmentOrderStatus",
  }),
  supportedActions: z.array(FulfillmentOrderSupportedActionSchema),
  locationsForMove: RawFulfillmentOrderLocationsForMoveSchema.nullish(),
  order: z.object({
    customDate1: z.object({ value: z.string().nullish() }).nullish(),
    customDate2: z.object({ value: z.string().nullish() }).nullish(),
    customDate3: z.object({ value: z.string().nullish() }).nullish(),
    customString1: z.object({ value: z.string().nullish() }).nullish(),
    customString2: z.object({ value: z.string().nullish() }).nullish(),
    customString3: z.object({ value: z.string().nullish() }).nullish(),
    customNumber1: z.object({ value: z.string().nullish() }).nullish(),
    customNumber2: z.object({ value: z.string().nullish() }).nullish(),
    customNumber3: z.object({ value: z.string().nullish() }).nullish(),
    redoShouldSplit: z.object({ value: z.string().nullish() }).nullish(),
    poNumber: z.string().nullish(),
    disputes: z
      .array(
        z.object({
          id: z.string(),
          initiatedAs: createEnum(RedoShopifyOrderDisputeInitiatedAs, {
            enumName: "RedoShopifyOrderDisputeInitiatedAs",
          }),
          status: createEnum(RedoShopifyOrderDisputeStatus, {
            enumName: "RedoShopifyOrderDisputeStatus",
          }),
        }),
      )
      .nullish(),
    billingAddress: z
      .object({
        address1: z.string().nullish(),
        address2: z.string().nullish(),
        city: z.string().nullish(),
        company: z.string().nullish(),
        countryCodeV2: createEnum(RedoShopifyCountryCode, {
          enumName: "RedoShopifyCountryCode",
        }).nullish(),
        name: z.string().nullish(),
        phone: z.string().nullish(),
        province: z.string().nullish(),
        zip: z.string().nullish(),
      })
      .nullish(),
    closed: z.boolean().nullish(),
    closedAt: z.coerce.date().nullish(),
    fullyPaid: z.boolean().nullish(),
    displayFinancialStatus: createEnum(RedoShopifyOrderDisplayFinancialStatus, {
      enumName: "RedoShopifyOrderDisplayFinancialStatus",
    }).nullish(),
    discountCodes: z.array(z.string()).nullish(),
    note: z.string().nullish(),
    customAttributes: z
      .array(z.object({ key: z.string(), value: z.string().nullish() }))
      .nullish(),
    tags: z.array(z.string()).nullish(),
    risk: RiskSchema.nullish(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    shippingLine: z
      .object({
        discountedPriceSet: MoneyBagWithPresentedSchema,
        title: z.string().nullish(),
        taxLines: z.array(z.object({ priceSet: MoneyBagSchema })),
      })
      .nullish(),
    shippingAddress: z
      .object({
        name: z.string().nullish(),
        company: z.string().nullish(),
        address1: z.string().nullish(),
        address2: z.string().nullish(),
        city: z.string().nullish(),
        province: z.string().nullish(),
        zip: z.string().nullish(),
        countryCodeV2: createEnum(RedoShopifyCountryCode, {
          enumName: "RedoShopifyCountryCode",
        }).nullish(),
        phone: z.string().nullish(),
      })
      .nullish(),
    transactions: z.array(
      z.object({
        id: z.string(),
        kind: createEnum(OrderTransactionKind, {
          enumName: "OrderTransactionKind",
        }),
        gateway: z.string().nullish(),
        formattedGateway: z.string().nullish(),
        paymentDetails: z
          .object({
            company: z.string().nullish(),
            number: z.string().nullish(),
          })
          .nullish(),
        amountSet: MoneyBagWithPresentedSchema,
        parentTransaction: z.object({ id: z.string() }).nullish(),
      }),
    ),
    customer: z
      .object({
        id: z.string(),
        email: z.string().nullish(),
        phone: z.string().nullish(),
        numberOfOrders: z.coerce.number(),
        displayName: z.string().nullish(),
        note: z.string().nullish(),
        tags: z.array(z.string()).nullish(),
      })
      .nullish(),
    purchasingEntity: z.object({ __typename: z.string().nullish() }).nullish(),
    channelInformation: z
      .object({
        app: z.object({ developerName: z.string().nullish() }),
        channelDefinition: z
          .object({ channelName: z.string().nullish() })
          .nullish(),
      })
      .nullish(),
    localizationExtensions: z
      .object({
        nodes: z.array(
          z.object({
            countryCode: z.string().nullish(),
            key: z.string().nullish(),
            purpose: createEnum(LocalizationExtensionPurpose, {
              enumName: "LocalizationExtensionPurpose",
            }),
            value: z.string(),
          }),
        ),
      })
      .nullish(),
  }),
});

export type RawFulfillmentOrder = z.infer<typeof RawFulfillmentOrderSchema>;

const RawFulfillmentLineItemSchema = z.object({
  id: z.string(),
  quantity: z.number().nullish(),
  lineItem: z.object({
    image: z.object({ url: z.string() }).nullish(),
    customAttributes: z.array(
      z.object({ key: z.string(), value: z.string().nullish() }),
    ),
    id: z.string(),
    discountedUnitPriceSet: MoneyBagSchema,
    originalUnitPriceSet: MoneyBagSchema,
    quantity: z.number(),
    currentQuantity: z.number(),
    variantTitle: z.string().nullish(),
    requiresShipping: z.boolean(),
    variant: z
      .object({
        displayName: z.string(),
        inventoryQuantity: z.number().nullish(),
        id: z.string(),
        bin: z.object({ value: z.string() }).nullish(),
        barcode: z.string().nullish(),
        product: z.object({
          id: z.string(),
          title: z.string(),
          vendor: z.string(),
          bin: z.object({ value: z.string() }).nullish(),
        }),
        selectedOptions: z.array(
          z.object({ name: z.string(), value: z.string() }),
        ),
        title: z.string(),
      })
      .nullish(),
    sku: z.string().nullish(),
    taxLines: z.array(z.object({ priceSet: MoneyBagSchema })),
  }),
});
export type RawFulfillmentLineItem = z.infer<
  typeof RawFulfillmentLineItemSchema
>;

const FulfillmentOriginAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  countryCode: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }),
  provinceCode: z.string().nullish(),
  zip: z.string().nullish(),
});

// Should match the requested fields from the fulfillment-fragments.ts
export const RawFulfillmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  originAddress: FulfillmentOriginAddressSchema.nullish(),
  location: z.object({ id: z.string() }).nullish(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  estimatedDeliveryAt: z.coerce.date().nullish(),
  inTransitAt: z.coerce.date().nullish(),
  deliveredAt: z.coerce.date().nullish(),
  status: createEnum(RedoShopifyFulfillmentStatus, {
    enumName: "RedoShopifyFulfillmentStatus",
  }),
  displayStatus: createEnum(RedoShopifyFulfillmentDisplayStatus, {
    enumName: "RedoShopifyFulfillmentDisplayStatus",
  }).nullish(),
  requiresShipping: z.boolean(),
  totalQuantity: z.number(),
  fulfillmentLineItems: z.array(RawFulfillmentLineItemSchema),
  trackingInfo: z.array(
    z.object({
      company: z.string().nullish(),
      url: z.string().nullish(),
      number: z.string().nullish(),
    }),
  ),
  order: z.object({
    id: z.string(),
    name: z.string(),
    note: z.string().nullish(),
    tags: z.array(z.string()).nullish(),
    createdAt: z.coerce.date(),
    shippingAddress: z
      .object({
        address1: z.string().nullish(),
        address2: z.string().nullish(),
        city: z.string().nullish(),
        company: z.string().nullish(),
        countryCodeV2: createEnum(RedoShopifyCountryCode, {
          enumName: "RedoShopifyCountryCode",
        }).nullish(),
        email: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        phone: z.string().nullish(),
        province: z.string().nullish(),
        zip: z.string().nullish(),
      })
      .nullish(),
    customer: z
      .object({
        id: z.string(),
        email: z.string().nullish(),
        phone: z.string().nullish(),
        numberOfOrders: z.coerce.number(),
        displayName: z.string().nullish(),
        note: z.string().nullish(),
        tags: z.array(z.string()).nullish(),
      })
      .nullish(),
    localizationExtensions: z
      .object({
        nodes: z.array(
          z.object({
            purpose: createEnum(LocalizationExtensionPurpose, {
              enumName: "LocalizationExtensionPurpose",
            }),
            value: z.string(),
          }),
        ),
      })
      .nullish(),
  }),
});
export type RawFulfillment = z.infer<typeof RawFulfillmentSchema>;

export const RawFulfillmentOrderSyncDataSchema = z.object({
  id: z.string(),
  updatedAt: z.coerce.date(),
  order: z.object({
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    tags: z.array(z.string()).nullish(),
  }),
});

export type RawFulfillmentOrderSyncData = z.infer<
  typeof RawFulfillmentOrderSyncDataSchema
>;

export const RawProductUpdateSyncDataSchema = z.object({
  id: z.string(),
  updatedAt: z.coerce.date(),
  bin: z.object({ value: z.string() }).nullish(),
  tags: z.array(z.string()).nullish(),
  title: z.string().nullish(),
  variants: z.object({
    nodes: z.array(
      z.object({
        id: z.string(),
        bin: z.object({ value: z.string() }).nullish(),
        barcode: z.string().nullish(),
        sku: z.string().nullish(),
        inventoryItem: z
          .object({
            measurement: z.object({ weight: WeightSchema.nullish() }).nullish(),
          })
          .nullish(),
        variantCustomDate1: z.object({ value: z.string() }).nullish(),
        variantCustomDate2: z.object({ value: z.string() }).nullish(),
        variantCustomDate3: z.object({ value: z.string() }).nullish(),
        variantCustomString1: z.object({ value: z.string() }).nullish(),
        variantCustomString2: z.object({ value: z.string() }).nullish(),
        variantCustomString3: z.object({ value: z.string() }).nullish(),
        variantCustomNumber1: z.object({ value: z.string() }).nullish(),
        variantCustomNumber2: z.object({ value: z.string() }).nullish(),
        variantCustomNumber3: z.object({ value: z.string() }).nullish(),
      }),
    ),
  }),
});
export type RawProductUpdateSyncData = z.infer<
  typeof RawProductUpdateSyncDataSchema
>;

export enum ShopifyRiskAssessmentResult {
  /** Indicates a high likelihood that the order is fraudulent. */
  High = "HIGH",
  /** Indicates a low likelihood that the order is fraudulent. */
  Low = "LOW",
  /** Indicates a medium likelihood that the order is fraudulent. */
  Medium = "MEDIUM",
  /** Indicates that the risk assessment will not provide a recommendation for the order. */
  None = "NONE",
  /** Indicates that the risk assessment is still pending. */
  Pending = "PENDING",
}

export const riskLabels: Record<ShopifyRiskAssessmentResult, string> = {
  [ShopifyRiskAssessmentResult.High]: "High",
  [ShopifyRiskAssessmentResult.Low]: "Low",
  [ShopifyRiskAssessmentResult.Medium]: "Medium",
  [ShopifyRiskAssessmentResult.None]: "None",
  [ShopifyRiskAssessmentResult.Pending]: "Pending",
};

export enum ShopifyFulfillmentHoldReason {
  /** The fulfillment hold is applied because payment is pending. */
  AwaitingPayment = "AWAITING_PAYMENT",
  /** The fulfillment hold is applied because of return items not yet received during an exchange. */
  AwaitingReturnItems = "AWAITING_RETURN_ITEMS",
  /** The fulfillment hold is applied because of a high risk of fraud. */
  HighRiskOfFraud = "HIGH_RISK_OF_FRAUD",
  /** The fulfillment hold is applied because of an incorrect address. */
  IncorrectAddress = "INCORRECT_ADDRESS",
  /** The fulfillment hold is applied because inventory is out of stock. */
  InventoryOutOfStock = "INVENTORY_OUT_OF_STOCK",
  /** The fulfillment hold is applied because of a post purchase upsell offer. */
  OnlineStorePostPurchaseCrossSell = "ONLINE_STORE_POST_PURCHASE_CROSS_SELL",
  /** The fulfillment hold is applied for another reason. */
  Other = "OTHER",
  /** The fulfillment hold is applied because of an unknown delivery date. */
  UnknownDeliveryDate = "UNKNOWN_DELIVERY_DATE",
}

export const ShopifyFulfillmentHoldDisplayNames: Record<
  ShopifyFulfillmentHoldReason,
  string
> = {
  [ShopifyFulfillmentHoldReason.AwaitingPayment]: "Awaiting Payment",
  [ShopifyFulfillmentHoldReason.AwaitingReturnItems]: "Awaiting Return Items",
  [ShopifyFulfillmentHoldReason.HighRiskOfFraud]: "High Risk of Fraud",
  [ShopifyFulfillmentHoldReason.IncorrectAddress]: "Incorrect Address",
  [ShopifyFulfillmentHoldReason.InventoryOutOfStock]: "Inventory Out of Stock",
  [ShopifyFulfillmentHoldReason.OnlineStorePostPurchaseCrossSell]:
    "Online Store Post Purchase Cross Sell",
  [ShopifyFulfillmentHoldReason.Other]: "Other",
  [ShopifyFulfillmentHoldReason.UnknownDeliveryDate]: "Unknown Delivery Date",
};
